import { useTranslation } from 'react-i18next';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { PageType } from '../types';
import { useSettings } from '../contexts/SettingsContext';
import { getNextJobNumber } from '../utils';
import AirflowPattern from './AirflowPattern';

interface LandingPageProps {
  onNavigate: (page: PageType) => void;
  savedOffersCount: number;
  savedWorkOrdersCount: number;
}

const LandingPage = ({ onNavigate, savedOffersCount, savedWorkOrdersCount }: LandingPageProps) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const nextJobNumber = getNextJobNumber(settings.userIdentityNumber);

  return (
    <div className="landing-page">
      {/* Hero Section with Halton Brand Elements */}
      <div className="landing-hero">
        <AirflowPattern variant="light" />
        <div className="hero-content">
          <h1>{t('landing.welcome')}</h1>
          <p>{t('landing.subtitle')}</p>
        </div>
        <div className="hero-gradient-overlay" />
      </div>

      {/* Feature Cards Grid */}
      <section className="feature-section">
        <div className="feature-grid">
          <div className="feature-card feature-card-calculator" onClick={() => onNavigate('calculator')}>
            <div className="feature-icon">
              <span className="material-symbols-outlined">calculate</span>
            </div>
            <h2 className="feature-title">{t('landing.calculatorCard.title')}</h2>
            <p className="feature-description">
              {t('landing.calculatorCard.description')}
            </p>
            <Button theme="primary" label={t('landing.calculatorCard.action')} icon="arrow-right" iconPosition="end" className="btn-lg" />
          </div>

          <div className="feature-card feature-card-workorder" onClick={() => onNavigate('workorder')}>
            <div className="feature-icon">
              <span className="material-symbols-outlined">assignment</span>
            </div>
            <h2 className="feature-title">{t('landing.workOrderCard.title')}</h2>
            <p className="feature-description">
              {t('landing.workOrderCard.description')}
            </p>
            <Button theme="primary" label={t('landing.workOrderCard.action')} icon="arrow-right" iconPosition="end" className="btn-lg" />
          </div>

          <div className="feature-card feature-card-maintenance" onClick={() => onNavigate('maintenance')}>
            <Chip label="BETA" theme="warning" size="small" className="feature-chip" />
            <div className="feature-icon">
              <span className="material-symbols-outlined">build</span>
            </div>
            <h2 className="feature-title">{t('landing.maintenanceCard.title')}</h2>
            <p className="feature-description">
              {t('landing.maintenanceCard.description')}
            </p>
            <Button theme="primary" label={t('landing.maintenanceCard.action')} icon="arrow-right" iconPosition="end" className="btn-lg" />
          </div>

          <div className="feature-card feature-card-secondary" onClick={() => onNavigate('archive')}>
            <div className="feature-icon">
              <span className="material-symbols-outlined">inventory_2</span>
            </div>
            <h2 className="feature-title">{t('landing.archiveCard.title')}</h2>
            <p className="feature-description">
              {t('landing.archiveCard.description')}
            </p>
            <Button theme="primary" label={t('landing.archiveCard.action')} icon="arrow-right" iconPosition="end" className="btn-lg" />
          </div>
        </div>
      </section>

      {/* Quick Stats Section */}
      <div className="quick-stats">
        <AirflowPattern variant="light" />
        <div className="quick-stats-content">
          <div className="stat-item">
            <div className="stat-value">{savedOffersCount}</div>
            <div className="stat-label">{t('landing.quickStats.quotes')}</div>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <div className="stat-value">{savedWorkOrdersCount}</div>
            <div className="stat-label">{t('landing.quickStats.workOrders')}</div>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <div className="stat-value stat-value-job">{nextJobNumber}</div>
            <div className="stat-label">{t('landing.quickStats.nextJobNumber')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
